import { useEffect, useRef, useState } from "react";

// Full-screen fixed video whose playhead is driven by scroll position.
export default function ScrollVideoBackground() {
  const videoRef = useRef(null);
  const target = useRef(0);
  const current = useRef(0);
  const duration = useRef(0);
  const raf = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const progress = () => {
      const top = window.scrollY || document.documentElement.scrollTop || 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return max > 0 ? Math.min(1, Math.max(0, top / max)) : 0;
    };

    const onMeta = () => {
      duration.current = video.duration || 0;
      try {
        video.currentTime = progress() * duration.current;
      } catch (e) {}
    };
    const onError = () => setFailed(true);

    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("error", onError);
    if (video.readyState >= 1) onMeta();

    // Robust decode-failure detection: if metadata never arrives, fall back.
    const failTimer = setTimeout(() => {
      if (!(video.duration > 0) && video.readyState < 1) setFailed(true);
    }, 6000);

    if (reduce) {
      const onScroll = () => {
        if (duration.current) {
          try {
            video.currentTime = progress() * duration.current;
          } catch (e) {}
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        clearTimeout(failTimer);
        video.removeEventListener("loadedmetadata", onMeta);
        video.removeEventListener("error", onError);
        window.removeEventListener("scroll", onScroll);
      };
    }

    const tick = () => {
      const dur = duration.current;
      if (dur && document.visibilityState === "visible") {
        target.current = progress() * dur;
        current.current += (target.current - current.current) * 0.12;
        if (Math.abs(target.current - current.current) < 0.004)
          current.current = target.current;
        if (!video.seeking) {
          try {
            video.currentTime = current.current;
          } catch (e) {}
        }
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);

    return () => {
      clearTimeout(failTimer);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("error", onError);
      cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <div
      data-testid="scroll-video-bg"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      aria-hidden="true"
    >
      <video
        ref={videoRef}
        muted
        playsInline
        preload="auto"
        className={`h-full w-full object-cover transition-opacity duration-700 ${
          failed ? "opacity-0" : "opacity-100"
        }`}
      >
        <source src="/bg-video.webm" type="video/webm" />
        <source src="/bg-video.mp4" type="video/mp4" />
      </video>
      {/* legibility overlays (double as fallback background if decode fails) */}
      <div className="absolute inset-0 bg-bg-primary/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-bg-primary/15 via-bg-primary/45 to-bg-primary/90" />
      {/* subtle brand glow so a decode failure still looks intentional */}
      <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-signal/[0.06] blur-[130px]" />
    </div>
  );
}
